import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { logout } from "../redux/slice/Auth";
import DashboardLayout from "../components/DashboardLayout";
import { settingsAPI, profileAPI } from "../services/api";

export default function SettingsPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isAuthenticated } = useSelector((state) => state.Auth);

    const [profileData, setProfileData] = useState({});
    const [fetchingData, setFetchingData] = useState(true);
    const [passwordLoading, setPasswordLoading] = useState(false);

    useEffect(() => {
        const storedUser = localStorage.getItem("user");
        if (!storedUser && !isAuthenticated) {
            navigate("/login");
            return;
        }

        // Fetch profile and settings data from API
        const fetchData = async () => {
            try {
                setFetchingData(true);

                // Fetch profile data
                const profileResponse = await profileAPI.getProfile();
                if (profileResponse.data.success) {
                    setProfileData(profileResponse.data.data);
                }

                // Fetch settings data
                const settingsResponse = await settingsAPI.getSettings();
                if (settingsResponse.data.success) {
                    const data = settingsResponse.data.data;
                    setNotifications(data.notifications);
                    setPrivacy(data.privacy);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                if (error.response?.status !== 404) {
                    toast.error("Failed to load settings");
                }
            } finally {
                setFetchingData(false);
            }
        };

        fetchData();
    }, [navigate, isAuthenticated]);

    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const [notifications, setNotifications] = useState({
        emailNotifications: true,
        smsNotifications: false,
        newLeads: true,
        clientMessages: true,
        bookingUpdates: true,
        weeklyReport: false,
    });

    const [privacy, setPrivacy] = useState({
        profileVisibility: "public",
        showEmail: false,
        showPhone: true,
        allowMessages: true,
    });

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
            toast.error("Please fill all password fields");
            return;
        }

        if (passwordData.newPassword.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.error("New passwords do not match");
            return;
        }

        setPasswordLoading(true);
        try {
            const response = await settingsAPI.changePassword({
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
                newPassword_confirmation: passwordData.confirmPassword,
            });

            if (response.data.success) {
                toast.success("Password changed successfully!");
                setPasswordData({
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                });
            } else {
                toast.error(response.data.message || "Failed to change password");
            }
        } catch (error) {
            console.error("Error changing password:", error);
            toast.error(
                error.response?.data?.message ||
                "Failed to change password. Please try again."
            );
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleNotificationToggle = async (key) => {
        const updatedNotifications = {
            ...notifications,
            [key]: !notifications[key],
        };

        setNotifications(updatedNotifications);

        try {
            const response = await settingsAPI.updateNotifications(updatedNotifications);
            if (response.data.success) {
                toast.success("Notification preferences updated");
            }
        } catch (error) {
            console.error("Error updating notifications:", error);
            // Revert on error
            setNotifications(notifications);
            toast.error("Failed to update notification preferences");
        }
    };

    const handlePrivacyToggle = async (key) => {
        const updatedPrivacy = {
            ...privacy,
            [key]: !privacy[key],
        };

        setPrivacy(updatedPrivacy);

        try {
            const response = await settingsAPI.updatePrivacy(updatedPrivacy);
            if (response.data.success) {
                toast.success("Privacy settings updated");
            }
        } catch (error) {
            console.error("Error updating privacy:", error);
            // Revert on error
            setPrivacy(privacy);
            toast.error("Failed to update privacy settings");
        }
    };

    const handlePrivacyChange = async (e) => {
        const updatedPrivacy = {
            ...privacy,
            profileVisibility: e.target.value,
        };

        setPrivacy(updatedPrivacy);

        try {
            const response = await settingsAPI.updatePrivacy(updatedPrivacy);
            if (response.data.success) {
                toast.success("Profile visibility updated");
            }
        } catch (error) {
            console.error("Error updating privacy:", error);
            // Revert on error
            setPrivacy(privacy);
            toast.error("Failed to update privacy settings");
        }
    };

    const handleDeleteAccount = async () => {
        const confirmed = window.confirm(
            "Are you sure you want to delete your account? This action cannot be undone."
        );
        if (confirmed) {
            const password = window.prompt(
                "Please enter your password to confirm account deletion:"
            );
            if (password) {
                try {
                    const response = await settingsAPI.deleteAccount(password);
                    if (response.data.success) {
                        dispatch(logout());
                        toast.success("Account deleted successfully");
                        navigate("/login");
                    }
                } catch (error) {
                    console.error("Error deleting account:", error);
                    toast.error(
                        error.response?.data?.message ||
                        "Failed to delete account. Please check your password."
                    );
                }
            }
        }
    };

    const handleLogout = () => {
        dispatch(logout());
        toast.success("Logged out successfully!");
        navigate("/login");
    };

    return (
        <DashboardLayout activeTab="settings">
            <div className="settings-tab">
                <div className="tab-header">
                    <div>
                        <h3>
                            <i className="bi bi-gear-fill me-2"></i>
                            Settings
                        </h3>
                        <p className="text-muted">Manage your account settings and preferences</p>
                    </div>
                </div>

                {fetchingData ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-danger" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted mt-3">Loading settings...</p>
                    </div>
                ) : (
                    <>
                        {/* Account Information */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-person-circle me-2"></i>
                                Account Information
                            </h5>
                            <div className="account-info-grid">
                                <div className="info-item">
                                    <label>Name</label>
                                    <p>{profileData.name || "Not set"}</p>
                                </div>
                                <div className="info-item">
                                    <label>Email</label>
                                    <p>{profileData.email || "Not set"}</p>
                                </div>
                                <div className="info-item">
                                    <label>Phone</label>
                                    <p>{profileData.phone || "Not set"}</p>
                                </div>
                                <div className="info-item">
                                    <label>City</label>
                                    <p>{profileData?.get_trainer?.city || "Not set"}</p>
                                </div>
                            </div>
                        </div>

                        {/* Change Password */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-shield-lock me-2"></i>
                                Change Password
                            </h5>
                            <form onSubmit={handlePasswordSubmit}>
                                <div className="row g-3">
                                    <div className="col-md-12">
                                        <label htmlFor="currentPassword" className="form-label">
                                            Current Password
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="currentPassword"
                                            name="currentPassword"
                                            value={passwordData.currentPassword}
                                            onChange={handlePasswordChange}
                                            placeholder="Enter current password"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="newPassword" className="form-label">
                                            New Password
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="newPassword"
                                            name="newPassword"
                                            value={passwordData.newPassword}
                                            onChange={handlePasswordChange}
                                            placeholder="Enter new password"
                                        />
                                        <small className="text-muted">Minimum 8 characters</small>
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="confirmPassword" className="form-label">
                                            Confirm New Password
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            value={passwordData.confirmPassword}
                                            onChange={handlePasswordChange}
                                            placeholder="Confirm new password"
                                        />
                                    </div>
                                    <div className="col-12">
                                        <button type="submit" className="btn btn-danger" disabled={passwordLoading}>
                                            {passwordLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Updating...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-key me-2"></i>
                                                    Update Password
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Notification Preferences */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-bell-fill me-2"></i>
                                Notification Preferences
                            </h5>
                            <div className="settings-list">
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <strong>Email Notifications</strong>
                                        <p className="text-muted">Receive notifications via email</p>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={notifications.emailNotifications}
                                            onChange={() => handleNotificationToggle("emailNotifications")}
                                        />
                                    </div>
                                </div>
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <strong>SMS Notifications</strong>
                                        <p className="text-muted">Receive notifications via SMS</p>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={notifications.smsNotifications}
                                            onChange={() => handleNotificationToggle("smsNotifications")}
                                        />
                                    </div>
                                </div>
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <strong>New Leads</strong>
                                        <p className="text-muted">Get notified when you receive new leads</p>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={notifications.newLeads}
                                            onChange={() => handleNotificationToggle("newLeads")}
                                        />
                                    </div>
                                </div>
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <strong>Client Messages</strong>
                                        <p className="text-muted">Get notified of new client messages</p>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={notifications.clientMessages}
                                            onChange={() => handleNotificationToggle("clientMessages")}
                                        />
                                    </div>
                                </div>
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <strong>Booking Updates</strong>
                                        <p className="text-muted">Get notified of booking changes</p>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={notifications.bookingUpdates}
                                            onChange={() => handleNotificationToggle("bookingUpdates")}
                                        />
                                    </div>
                                </div>
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <strong>Weekly Report</strong>
                                        <p className="text-muted">Receive weekly performance reports</p>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={notifications.weeklyReport}
                                            onChange={() => handleNotificationToggle("weeklyReport")}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Privacy Settings */}
                        <div className="form-section">
                            <h5 className="section-title">
                                <i className="bi bi-eye-fill me-2"></i>
                                Privacy Settings
                            </h5>
                            <div className="settings-list">
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <strong>Profile Visibility</strong>
                                        <p className="text-muted">Control who can see your profile</p>
                                    </div>
                                    <select
                                        className="form-select"
                                        style={{ maxWidth: "200px" }}
                                        value={privacy.profileVisibility}
                                        onChange={handlePrivacyChange}
                                    >
                                        <option value="public">Public</option>
                                        <option value="private">Private</option>
                                        <option value="members">Members Only</option>
                                    </select>
                                </div>
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <strong>Show Email</strong>
                                        <p className="text-muted">Display email on public profile</p>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={privacy.showEmail}
                                            onChange={() => handlePrivacyToggle("showEmail")}
                                        />
                                    </div>
                                </div>
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <strong>Show Phone</strong>
                                        <p className="text-muted">Display phone number on public profile</p>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={privacy.showPhone}
                                            onChange={() => handlePrivacyToggle("showPhone")}
                                        />
                                    </div>
                                </div>
                                <div className="setting-item">
                                    <div className="setting-info">
                                        <strong>Allow Messages</strong>
                                        <p className="text-muted">Allow users to send you messages</p>
                                    </div>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={privacy.allowMessages}
                                            onChange={() => handlePrivacyToggle("allowMessages")}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Danger Zone */}
                        <div className="form-section danger-zone">
                            <h5 className="section-title text-danger">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                Danger Zone
                            </h5>
                            <div className="danger-actions">
                                <div className="danger-item">
                                    <div>
                                        <strong>Logout</strong>
                                        <p className="text-muted">Sign out of your account</p>
                                    </div>
                                    <button className="btn btn-outline-secondary" onClick={handleLogout}>
                                        <i className="bi bi-box-arrow-right me-2"></i>
                                        Logout
                                    </button>
                                </div>
                                <div className="danger-item">
                                    <div>
                                        <strong>Delete Account</strong>
                                        <p className="text-muted">Permanently delete your account and all data</p>
                                    </div>
                                    <button className="btn btn-danger" onClick={handleDeleteAccount}>
                                        <i className="bi bi-trash me-2"></i>
                                        Delete Account
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </DashboardLayout>
    );
}
